/**
 * Avatar Loop embed transport — iframe parity for the frozen loop events
 * (`AVATAR_LOOP_CONTRACTS.md` §7, additive 27 Aug 2026).
 *
 * The Avatar Loop lifecycle runs on window CustomEvents, which stop at the
 * iframe boundary. This module relays the FOUR frozen events across the
 * existing embed postMessage protocol so `<AvatarEmbed mode='iframe'>` hosts
 * participate in the loop exactly like native-mode hosts:
 *
 *   iframe → host page:  avatar:action:offer      as `avatar.loop.action.offer`
 *                        avatar:action:executed   as `avatar.loop.action.executed`
 *   host page → iframe:  avatar:action:response   as `loop.action.response`
 *                        avatar:live:contextUpdate as `loop.context.update`
 *
 * THE FROZEN PAYLOADS TRAVEL VERBATIM. The postMessage envelope is the
 * existing embedProtocol `{ type, payload }` shape; `payload` IS the frozen
 * event detail object, structured-cloned by postMessage and re-dispatched
 * unmodified as `CustomEvent.detail` on the far side. No renaming, no
 * re-shaping — a contract payload arrives byte-equivalent.
 *
 * NO NEW WINDOW EVENT NAMES (TM-2): the dotted names above are postMessage
 * envelope types in embedProtocol's own namespace (like `session.context` /
 * `avatar.ready`), not window events. On both sides the bridge re-dispatches
 * onto the SAME four frozen window event names.
 *
 * THE MOTOR BOUNDARY HOLDS ACROSS THE BOUNDARY: `receiveAvatarLoopEmbedCommand`
 * re-validates every inbound response before any window event is dispatched —
 * a forged/mistyped verb or a malformed detail is refused (surfaced via
 * `onRefused`, never dispatched), the same way `createHostActionRegistry
 * .execute` refuses invalid verbs. Behind that line the lifecycle itself
 * still refuses invalid verbs, executes only the OFFERED envelope (never the
 * response echo), and consumes each offerId at most once — so even a
 * well-shaped duplicate cannot re-execute.
 *
 * VERSIONING follows the embedProtocol convention: the protocol is additive
 * and dispatches on `type` — an older peer that predates these four types
 * ignores them (both receive functions return false for unknown types), and
 * an older iframe simply never emits them. ORIGIN checks are inherited from
 * the existing handlers that feed these receive functions (`AvatarEmbed`'s
 * message listener on the host side, `useMessageHandler`'s origin gate on
 * the iframe side) — the bridge is only ever handed origin-validated data.
 */

import {
  AVATAR_EMBED_COMMAND,
  AVATAR_EMBED_EVENT,
  isAvatarEmbedEnvelope,
  type AvatarEmbedCommandEnvelope,
  type AvatarEmbedEventEnvelope
} from '../host/embedProtocol';
import type {
  AvatarActionEnvelope,
  AvatarActionExecutedDetail,
  AvatarActionOfferDetail,
  AvatarActionResponseDetail,
  AvatarLoopContextUpdateDetail
} from './contracts';
import { addAvatarLoopEventListener, dispatchAvatarLoopEvent } from './events';
import { isAvatarActionVerb } from './hostActions';

/** Called when the bridge refuses an inbound envelope. Never throws back. */
export type AvatarLoopEmbedRefusalHandler = (reason: string, data: unknown) => void;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Runtime guard for the frozen `{ name, args }` action envelope shape. */
export function isAvatarActionEnvelopeShape(value: unknown): value is AvatarActionEnvelope {
  return isPlainObject(value)
    && typeof value.name === 'string'
    && isPlainObject(value.args);
}

/** Runtime guard for a wire-crossed `AvatarActionOfferDetail`. */
export function isAvatarActionOfferDetailShape(value: unknown): value is AvatarActionOfferDetail {
  return isPlainObject(value)
    && typeof value.offerId === 'string'
    && isAvatarActionEnvelopeShape(value.envelope);
}

/**
 * Runtime guard for a wire-crossed `AvatarActionResponseDetail`. The verb
 * check is the motor-boundary line: only the three frozen verbs pass, so a
 * forged `verb: 'execute'` (or anything else) never becomes a window event.
 */
export function isAvatarActionResponseDetailShape(
  value: unknown
): value is AvatarActionResponseDetail {
  return isPlainObject(value)
    && typeof value.offerId === 'string'
    && isAvatarActionVerb(value.verb)
    && isAvatarActionEnvelopeShape(value.envelope);
}

/**
 * Runtime guard for a wire-crossed `AvatarActionExecutedDetail` — exactly
 * `{ offerId, verb, executed, captured }`, where `captured` is the written
 * Timeline note or null when the capture write failed (§8). The executed
 * wire carries no envelope echo.
 */
export function isAvatarActionExecutedDetailShape(
  value: unknown
): value is AvatarActionExecutedDetail {
  return isPlainObject(value)
    && typeof value.offerId === 'string'
    && isAvatarActionVerb(value.verb)
    && typeof value.executed === 'boolean'
    && 'captured' in value
    && (value.captured === null || isPlainObject(value.captured));
}

/* =============================================================================
 * IFRAME SIDE (the embedded avatar page)
 * ========================================================================== */

/**
 * Iframe side, outbound: forward the avatar's `avatar:action:offer` and
 * `avatar:action:executed` window events to the embedding host page. The
 * frozen detail travels verbatim as the envelope payload. Returns the
 * unsubscribe function (the `AvatarHostAdapter.subscribe` convention).
 */
export function forwardAvatarLoopEventsToEmbedHost(
  post: (envelope: AvatarEmbedEventEnvelope) => void,
  target: EventTarget = window
): () => void {
  const unsubscribeOffer = addAvatarLoopEventListener(
    'avatar:action:offer',
    (detail) => {
      post({ type: AVATAR_EMBED_EVENT.loopActionOffer, payload: detail });
    },
    target
  );
  const unsubscribeExecuted = addAvatarLoopEventListener(
    'avatar:action:executed',
    (detail) => {
      post({ type: AVATAR_EMBED_EVENT.loopActionExecuted, payload: detail });
    },
    target
  );
  return () => {
    unsubscribeOffer();
    unsubscribeExecuted();
  };
}

/**
 * Iframe side, inbound: handle one loop COMMAND envelope from the host page
 * (already origin-validated by the caller). Valid payloads are re-dispatched
 * verbatim onto the frozen window events; everything else is refused without
 * dispatching anything. Returns true iff the envelope was a loop command
 * that validated and was dispatched.
 */
export function receiveAvatarLoopEmbedCommand(
  data: unknown,
  target: EventTarget = window,
  onRefused?: AvatarLoopEmbedRefusalHandler
): boolean {
  if (!isAvatarEmbedEnvelope(data)) {
    return false;
  }

  if (data.type === AVATAR_EMBED_COMMAND.loopActionResponse) {
    const payload = data.payload;
    if (!isAvatarActionResponseDetailShape(payload)) {
      // Motor boundary at the transport: a forged verb or malformed detail
      // never becomes an avatar:action:response event — refused exactly as
      // createHostActionRegistry.execute refuses invalid verbs.
      onRefused?.(
        `motor boundary: refusing '${AVATAR_EMBED_COMMAND.loopActionResponse}' — payload is not a valid AvatarActionResponseDetail (offerId, frozen verb, envelope required)`,
        data
      );
      return false;
    }
    dispatchAvatarLoopEvent('avatar:action:response', payload, target);
    return true;
  }

  if (data.type === AVATAR_EMBED_COMMAND.loopContextUpdate) {
    const payload = data.payload;
    // The TM-2 bridge forwards contextUpdate detail verbatim with no
    // allow-list; the embed hop mirrors that, refusing only non-object
    // payloads that could never be a detail.
    if (!isPlainObject(payload)) {
      onRefused?.(
        `refusing '${AVATAR_EMBED_COMMAND.loopContextUpdate}' — payload must be the contextUpdate detail object`,
        data
      );
      return false;
    }
    dispatchAvatarLoopEvent(
      'avatar:live:contextUpdate',
      payload as unknown as AvatarLoopContextUpdateDetail,
      target
    );
    return true;
  }

  return false;
}

/* =============================================================================
 * HOST SIDE (the page embedding the avatar iframe)
 * ========================================================================== */

/**
 * Host side, outbound: forward the host page's `avatar:action:response` and
 * `avatar:live:contextUpdate` window events into the iframe. The frozen
 * detail travels verbatim as the envelope payload; validation happens at the
 * receiving (iframe) side, which is the trust boundary. Returns the
 * unsubscribe function.
 */
export function forwardAvatarLoopEventsToEmbedIframe(
  post: (envelope: AvatarEmbedCommandEnvelope) => void,
  target: EventTarget = window
): () => void {
  const unsubscribeResponse = addAvatarLoopEventListener(
    'avatar:action:response',
    (detail) => {
      post({ type: AVATAR_EMBED_COMMAND.loopActionResponse, payload: detail });
    },
    target
  );
  const unsubscribeContext = addAvatarLoopEventListener(
    'avatar:live:contextUpdate',
    (detail) => {
      post({ type: AVATAR_EMBED_COMMAND.loopContextUpdate, payload: detail });
    },
    target
  );
  return () => {
    unsubscribeResponse();
    unsubscribeContext();
  };
}

/**
 * Host side, inbound: handle one loop EVENT envelope from the iframe
 * (already origin-validated by the caller). Valid payloads are re-dispatched
 * verbatim onto the frozen window events so host-page listeners
 * (`addAvatarLoopEventListener`) work unchanged. Returns true iff the
 * envelope was a loop event that validated and was dispatched.
 */
export function receiveAvatarLoopEmbedEvent(
  data: unknown,
  target: EventTarget = window,
  onRefused?: AvatarLoopEmbedRefusalHandler
): boolean {
  if (!isAvatarEmbedEnvelope(data)) {
    return false;
  }

  if (data.type === AVATAR_EMBED_EVENT.loopActionOffer) {
    const payload = data.payload;
    if (!isAvatarActionOfferDetailShape(payload)) {
      onRefused?.(
        `refusing '${AVATAR_EMBED_EVENT.loopActionOffer}' — payload is not a valid AvatarActionOfferDetail`,
        data
      );
      return false;
    }
    dispatchAvatarLoopEvent('avatar:action:offer', payload, target);
    return true;
  }

  if (data.type === AVATAR_EMBED_EVENT.loopActionExecuted) {
    const payload = data.payload;
    if (!isAvatarActionExecutedDetailShape(payload)) {
      onRefused?.(
        `refusing '${AVATAR_EMBED_EVENT.loopActionExecuted}' — payload is not a valid AvatarActionExecutedDetail`,
        data
      );
      return false;
    }
    dispatchAvatarLoopEvent('avatar:action:executed', payload, target);
    return true;
  }

  return false;
}
